import { useState } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { supabase } from '../lib/supabase';
import { useCategories } from '../hooks/useCategories';
import { t } from '../lib/i18n';
import EmojiPicker from '../components/EmojiPicker';
import '../components/EmojiPicker.css';
import './CategoryManagePage.css';

export default function CategoryManagePage({ user, onBack }) {
  const { categories, refetch } = useCategories(user.id);
  const [modal, setModal] = useState(null); // null | { mode: 'add'|'edit', cat? }
  const [editName, setEditName] = useState('');
  const [editEmoji, setEditEmoji] = useState('⭐');
  const [saving, setSaving] = useState(false);

  const openAdd = () => {
    setEditName('');
    setEditEmoji('⭐');
    setModal({ mode: 'add' });
  };

  const openEdit = (cat) => {
    setEditName(cat.name);
    setEditEmoji(cat.icon || '⭐');
    setModal({ mode: 'edit', cat });
  };

  const closeModal = () => setModal(null);

  const handleSave = async () => {
    if (!editName.trim()) return;
    setSaving(true);
    if (modal.mode === 'add') {
      const nextOrder = categories.length;
      await supabase.from('categories').insert({
        user_id: user.id,
        name: editName.trim(),
        icon: editEmoji,
        sort_order: nextOrder,
      });
    } else {
      await supabase.from('categories')
        .update({ name: editName.trim(), icon: editEmoji })
        .eq('id', modal.cat.id);
    }
    await refetch();
    setSaving(false);
    closeModal();
  };

  const handleDelete = async (cat) => {
    if (!window.confirm(`"${cat.name}" 카테고리를 삭제할까요?`)) return;
    await supabase.from('categories').delete().eq('id', cat.id);
    await refetch();
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;
    if (result.source.index === result.destination.index) return;

    const newOrder = [...categories];
    const [moved] = newOrder.splice(result.source.index, 1);
    newOrder.splice(result.destination.index, 0, moved);

    await Promise.all(
      newOrder.map((cat, i) =>
        supabase.from('categories').update({ sort_order: i }).eq('id', cat.id)
      )
    );
    await refetch();
  };

  return (
    <div className="cat-manage-page">
      <header className="cat-manage-header">
        <button className="header-back" onClick={onBack}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        <h2>{t('category_manage')}</h2>
        <button className="header-add-btn" onClick={openAdd}>+ {t('profile_add_btn')}</button>
      </header>

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="categories">
          {(provided) => (
            <ul
              className="cat-manage-list"
              ref={provided.innerRef}
              {...provided.droppableProps}
            >
              {categories.map((cat, index) => (
                <Draggable key={cat.id} draggableId={cat.id} index={index}>
                  {(provided, snapshot) => (
                    <li
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      className={`cat-manage-item${snapshot.isDragging ? ' cat-manage-item--dragging' : ''}`}
                    >
                      <span
                        className="cat-item-drag"
                        {...provided.dragHandleProps}
                        aria-label="순서 변경"
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                          <line x1="4" y1="7" x2="20" y2="7" />
                          <line x1="4" y1="12" x2="20" y2="12" />
                          <line x1="4" y1="17" x2="20" y2="17" />
                        </svg>
                      </span>
                      <span className="cat-item-emoji">{cat.icon || '📦'}</span>
                      <span className="cat-item-name">{cat.name}</span>
                      <div className="cat-item-actions">
                        <button className="btn-icon btn-edit" onClick={() => openEdit(cat)} aria-label="수정">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                          </svg>
                        </button>
                        <button className="btn-icon btn-delete" onClick={() => handleDelete(cat)} aria-label="삭제">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                            <path d="M10 11v6M14 11v6" />
                            <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                          </svg>
                        </button>
                      </div>
                    </li>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </ul>
          )}
        </Droppable>
      </DragDropContext>

      {modal && (
        <div className="modal-backdrop" onClick={closeModal}>
          <div className="modal-sheet" onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title">
              {modal.mode === 'add' ? '카테고리 추가' : '카테고리 수정'}
            </h3>
            <div className="modal-field">
              <label>이름</label>
              <input
                type="text"
                placeholder="카테고리 이름"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
              />
            </div>
            <div className="modal-field">
              <label>이모지</label>
              <EmojiPicker value={editEmoji} onChange={setEditEmoji} />
            </div>
            <div className="modal-actions">
              <button className="btn-modal-cancel" onClick={closeModal}>취소</button>
              <button
                className="btn-modal-save"
                onClick={handleSave}
                disabled={saving || !editName.trim()}
              >
                {saving ? '저장 중...' : '저장'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
